import React, { useState } from 'react';
import {
  faCircleExclamation,
  faTriangleExclamation,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { partitionTemplateIssues, renderTemplateIssue } from './templateIssues';

// Past this many, warnings start collapsed: a template with a blank electricity
// block on every row would otherwise push the rows it is about off the screen.
const WARNINGS_OPEN_LIMIT = 3;

const IssueList = ({ issues, className }) => (
  <ul className={`mt-1 space-y-0.5 list-disc pl-5 ${className}`}>
    {issues.map((issue, index) => (
      <li
        key={`${issue.code}-${index}`}
        className="whitespace-pre-line"
      >
        {renderTemplateIssue(issue)}
      </li>
    ))}
  </ul>
);

const rowErrorTitle = (t, outcome, count) => {
  if (outcome === 'imported') {
    return t('templateIssues.rowErrorTitleImported', {
      count,
      defaultValue: '{{count}} row was not imported',
      defaultValue_other: '{{count}} rows were not imported',
    });
  }
  if (outcome === 'refused') {
    return t('templateIssues.rowErrorTitleRefused', {
      count,
      defaultValue: '{{count}} problem must be corrected before this file can be accepted',
      defaultValue_other: '{{count}} problems must be corrected before this file can be accepted',
    });
  }
  return t('templateIssues.rowErrorTitle', {
    count,
    defaultValue: '{{count}} problem means a row will not be imported',
    defaultValue_other: '{{count}} problems mean rows will not be imported',
  });
};

const rowErrorHint = (t, outcome) => {
  if (outcome === 'imported') {
    return t(
      'templateIssues.rowErrorHintImported',
      'Correct these rows in the workbook and upload it again to add them.',
    );
  }
  if (outcome === 'refused') {
    return t('templateIssues.fixInWorkbook', 'Correct the workbook and upload it again.');
  }
  return t(
    'templateIssues.rowErrorHint',
    'Correct these rows in the workbook and upload it again, or continue without them.',
  );
};

/**
 * Everything the parser reported about one template, in the order the user has
 * to deal with it: what stops the file, which rows were left out, what was
 * assumed. The reporting-period issue is NOT shown here — `ReportingPeriodPrompt`
 * owns it, because it comes with something to do.
 *
 * Renders nothing when there is nothing to say.
 *
 * `outcome` says where the file stands, which decides what the user is told:
 *   - 'pending'  (default) nothing is saved yet; rows can be left out or fixed.
 *   - 'imported' the readable rows are already saved; the rest were not.
 *   - 'refused'  the whole file was refused (the supplier portal accepts complete
 *                files only). The period and choices are not offered there, so
 *                those issues are listed here too, as things to correct.
 */
const TemplateIssuesPanel = ({ parsedData, outcome = 'pending', className = '' }) => {
  const { t } = useTranslation('common');
  const { fileErrors, rowErrors, warnings } = partitionTemplateIssues(parsedData, {
    resolvable: outcome !== 'refused',
  });
  const [warningsOpen, setWarningsOpen] = useState(warnings.length <= WARNINGS_OPEN_LIMIT);

  if (fileErrors.length + rowErrors.length + warnings.length === 0) return null;

  return (
    <div
      className={`space-y-3 ${className}`}
      data-testid="template-issues"
    >
      {fileErrors.length > 0 && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className="text-red-500 mt-0.5 shrink-0"
          />
          <div className="flex-1 text-sm">
            <p className="font-medium text-red-800">
              {t('templateIssues.fileErrorTitle', "This file can't be used as it is")}
            </p>
            <IssueList
              issues={fileErrors}
              className="text-red-700"
            />
            <p className="text-red-700 mt-2">
              {t('templateIssues.fixInWorkbook', 'Correct the workbook and upload it again.')}
            </p>
          </div>
        </div>
      )}

      {rowErrors.length > 0 && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className="text-red-500 mt-0.5 shrink-0"
          />
          <div className="flex-1 text-sm">
            <p className="font-medium text-red-800">
              {rowErrorTitle(t, outcome, rowErrors.length)}
            </p>
            <IssueList
              issues={rowErrors}
              className="text-red-700"
            />
            <p className="text-red-700 mt-2">{rowErrorHint(t, outcome)}</p>
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="text-amber-500 mt-0.5 shrink-0"
          />
          <div className="flex-1 text-sm">
            <button
              type="button"
              onClick={() => setWarningsOpen((open) => !open)}
              aria-expanded={warningsOpen}
              className="font-medium text-amber-800 flex items-center gap-2 text-left"
            >
              <FontAwesomeIcon
                icon={warningsOpen ? faChevronDown : faChevronRight}
                className="text-xs"
              />
              {t('templateIssues.warningTitle', {
                count: warnings.length,
                defaultValue: '{{count}} value was assumed while reading this file',
                defaultValue_other: '{{count}} values were assumed while reading this file',
              })}
            </button>
            {warningsOpen && (
              <IssueList
                issues={warnings}
                className="text-amber-700"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateIssuesPanel;
